import { AbstractService, ApiResponse, PaginatedResponse } from './core/AbstractService';
import { Product } from './core/types';
import api from './core/api';

class ProductService extends AbstractService<Product> {
  constructor() {
    super('/products');
  }

  /**
   * Get all products. The admin panel manages every product, including
   * out-of-stock ones that the public storefront hides by default, so
   * `includeOutOfStock` is always sent unless the caller explicitly opts out.
   */
  async getAll(params?: URLSearchParams): Promise<PaginatedResponse<Product>> {
    const finalParams = new URLSearchParams(params);
    if (!finalParams.has('includeOutOfStock')) {
      finalParams.set('includeOutOfStock', 'true');
    }
    return super.getAll(finalParams);
  }

  /**
   * Get product by slug
   */
  async getBySlug(slug: string): Promise<ApiResponse<Product>> {
    const response = await api.get<ApiResponse<Product>>(
      `${this.basePath}/slug/${slug}`
    );
    return response.data;
  }

  /**
   * Check if a slug is available
   * @param slug - The slug to check
   * @param excludeProductId - Optional product ID to exclude from the check (for edit mode)
   */
  async checkSlugAvailability(slug: string, excludeProductId?: string): Promise<boolean> {
    try {
      const response = await api.get<ApiResponse<Product>>(`${this.basePath}/slug/${slug}`);
      // If product exists, check if it's the same product being edited
      if (excludeProductId && response.data.data?.id === excludeProductId) {
        return true; // Same product, slug is available
      }
      // Different product has this slug
      return false;
    } catch (error: any) {
      // 404 means slug is available
      if (error.response?.status === 404) {
        return true;
      }
      throw error;
    }
  }

  /**
   * Search products with filters
   */
  async searchProducts(searchParams: {
    search?: string;
    category?: string;
    featured?: boolean;
    bestseller?: boolean;
    minPrice?: number;
    maxPrice?: number;
    inStock?: boolean;
    tags?: string[];
    page?: number;
    limit?: number;
    sortBy?: string;
    sortDirection?: 'asc' | 'desc';
  }) {
    const params = new URLSearchParams();

    if (searchParams.search) params.set('search', searchParams.search);
    if (searchParams.category) params.set('category', searchParams.category);
    if (searchParams.featured !== undefined)
      params.set('featured', String(searchParams.featured));
    if (searchParams.bestseller !== undefined)
      params.set('bestseller', String(searchParams.bestseller));
    if (searchParams.minPrice)
      params.set('minPrice', String(searchParams.minPrice));
    if (searchParams.maxPrice)
      params.set('maxPrice', String(searchParams.maxPrice));
    if (searchParams.inStock !== undefined)
      params.set('inStock', String(searchParams.inStock));
    if (searchParams.tags?.length) params.set('tags', searchParams.tags.join(','));
    if (searchParams.page) params.set('page', String(searchParams.page));
    if (searchParams.limit) params.set('limit', String(searchParams.limit));
    if (searchParams.sortBy) params.set('sortBy', searchParams.sortBy);
    if (searchParams.sortDirection)
      params.set('sortDirection', searchParams.sortDirection);

    return this.getAll(params);
  }

  /**
   * Trigger a Finplus product sync. The backend starts the sync in the background and
   * responds immediately (a full run checking every in-stock barcode can take minutes),
   * so this only confirms the sync started — poll getFinplusSyncStatus for the result.
   */
  async syncFinplus(): Promise<ApiResponse<FinplusSyncStatus>> {
    const response = await api.post<ApiResponse<FinplusSyncStatus>>(`${this.basePath}/admin/finplus-sync`);
    return response.data;
  }

  /**
   * Poll this for Finplus sync progress/result after calling syncFinplus().
   */
  async getFinplusSyncStatus(): Promise<ApiResponse<FinplusSyncStatus>> {
    const response = await api.get<ApiResponse<FinplusSyncStatus>>(`${this.basePath}/admin/finplus-sync/status`);
    return response.data;
  }

  /**
   * Apply a % discount to every in-stock product whose existing discount (if any) is
   * smaller than this percentage; products with an equal or bigger existing discount
   * are left untouched. The backend runs this in the background and responds
   * immediately, so poll getBulkDiscountStatus for the result.
   */
  async applyBulkDiscount(percentage: number): Promise<ApiResponse<BulkDiscountStatus>> {
    const response = await api.post<ApiResponse<BulkDiscountStatus>>(`${this.basePath}/admin/bulk-discount`, {
      percentage,
    });
    return response.data;
  }

  /**
   * Poll this for bulk discount progress/result after calling applyBulkDiscount().
   */
  async getBulkDiscountStatus(): Promise<ApiResponse<BulkDiscountStatus>> {
    const response = await api.get<ApiResponse<BulkDiscountStatus>>(`${this.basePath}/admin/bulk-discount/status`);
    return response.data;
  }
}

export interface FinplusSyncResult {
  barcodesChecked: number;
  barcodesOutOfStock: number;
  productsMarkedOutOfStock: number;
  variantsMarkedOutOfStock: number;
  errors: { barcode: string; message: string }[];
}

export interface FinplusSyncStatus {
  inProgress: boolean;
  lastStartedAt: string | null;
  lastCompletedAt: string | null;
  lastResult: FinplusSyncResult | null;
  lastError: string | null;
}

export interface BulkDiscountResult {
  percentage: number;
  productsEligible: number;
  productsUpdated: number;
  productsSkipped: number;
  errors: { productId: string; message: string }[];
}

export interface BulkDiscountStatus {
  inProgress: boolean;
  lastStartedAt: string | null;
  lastCompletedAt: string | null;
  lastResult: BulkDiscountResult | null;
  lastError: string | null;
}

export const productService = new ProductService();
export default productService;
